
import React, { useState, useEffect } from 'react';
import { Menu, X, ShoppingBag, User, LogOut, Scissors, ArrowRight, Sparkles, Music, Calendar, Briefcase, Activity, Settings, Palette, Star } from 'lucide-react';
import { signOut, supabase } from '../services/supabaseClient';

interface NavbarProps {
  cartCount: number;
  onNavigate: (page: string) => void;
  currentPage: string;
  session?: any;
  themeLayout?: 'classic' | 'editorial' | 'urban';
  logoPosition?: 'inline' | 'centered' | 'stacked' | 'inline_big';
  logoUrl?: string;
  tejiendoLogoUrl?: string;
  isStorefront?: boolean;
  siteConfig?: any;
}

export const Navbar: React.FC<NavbarProps> = ({
  cartCount,
  onNavigate,
  currentPage,
  session,
  themeLayout = 'classic',
  logoPosition = 'inline',
  logoUrl,
  tejiendoLogoUrl,
  isStorefront = false,
  siteConfig
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileUserMenuOpen, setIsMobileUserMenuOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [apiStatus, setApiStatus] = useState<'idle' | 'busy'>('idle');

  // LOGO ESPECÍFICO PARA "TEJIENDO CON MABELITA" (Fallback al hardcoded si no hay config)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // API STATUS MONITOR (Solo admin — ahorra egress Realtime para usuarios normales)
  const [userIsAdmin, setUserIsAdmin] = useState(false);
  useEffect(() => {
    if (!session?.user) { setUserIsAdmin(false); return; }
    supabase.from('profiles').select('role').eq('id', session.user.id).single()
      .then(({ data }) => setUserIsAdmin(data?.role === 'admin'));
  }, [session]);

  useEffect(() => {
    if (userIsAdmin) {
      const channel = supabase
        .channel('api_status')
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'api_logs' },
          (payload) => {
            setApiStatus('busy');
            setTimeout(() => setApiStatus('idle'), 2000);
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [userIsAdmin]);

  const handleLogout = async () => {
    await signOut();
    onNavigate(isStorefront ? 'tejiendo' : 'home');
  };

  // Smooth scroll to anchor section within single-page landing
  const handleNavClick = (link: any) => {
    if (link.anchor) {
      const el = document.getElementById(link.anchor);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // If section not found, navigate to the page first, then scroll
        onNavigate(link.path);
        setTimeout(() => {
          const target = document.getElementById(link.anchor);
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      }
    } else {
      onNavigate(link.path);
    }
  };

  // NUEVA ESTRUCTURA DE MENÚ DINÁMICA
  const navLinks = isStorefront ? [
    { name: 'La Tejedora', path: 'tejiendo', anchor: 'artista', hasMenu: false },
    { name: 'Galería', path: 'tejiendo', anchor: 'galeria', hasMenu: false },
    { name: 'Testimonios', path: 'tejiendo', anchor: 'testimonios', hasMenu: false },
    { name: 'Encargos', path: 'tejiendo', anchor: 'encargos', hasMenu: false },
    { name: 'Tienda', path: 'tienda', hasMenu: false }
  ] : [
    { name: 'La Artista', path: 'home', anchor: 'artista', hasMenu: false },
    { name: 'Actuaciones', path: 'home', anchor: 'actuaciones', hasMenu: false },
    { name: 'Contratar', path: 'home', anchor: 'contratar', hasMenu: false },
    { name: 'Testimonios', path: 'home', anchor: 'testimonios', hasMenu: false },
    { name: 'Tienda', path: 'tienda', hasMenu: false }
  ];

  // --- LOGIC DE CONTRASTE GARANTIZADO ---
  const isHome = currentPage === 'home';

  // Determinar estado visual
  let navBackgroundClass = 'bg-transparent';
  let textColorClass = 'text-white';
  let logoColorClass = 'text-white';
  let iconColorClass = 'text-white';
  let marqueeClass = 'bg-mabelita-primary text-white';

  if (!isHome || isScrolled) {
    // Estado Navegación Estándar
    if (themeLayout === 'urban') {
      navBackgroundClass = 'bg-[#121212] border-b-4 border-[#ccff00] py-2';
      textColorClass = 'text-white';
      logoColorClass = 'text-[#ccff00]';
      iconColorClass = 'text-white';
      marqueeClass = 'bg-[#ccff00] text-black border-b-2 border-black';
    } else {
      // Classic & Editorial
      navBackgroundClass = 'bg-white/95 backdrop-blur-md shadow-md py-2';
      textColorClass = 'text-mabelita-dark';
      logoColorClass = 'text-mabelita-primary';
      iconColorClass = 'text-mabelita-dark';
      marqueeClass = 'bg-mabelita-dark text-white';
    }
  } else {
    // Estado Home Top (Transparente)
    if (themeLayout === 'editorial' || themeLayout === 'classic') {
      textColorClass = 'text-mabelita-dark';
      logoColorClass = 'text-mabelita-primary';
      iconColorClass = 'text-mabelita-dark';
      marqueeClass = themeLayout === 'classic' ? 'bg-gradient-to-r from-mabelita-primary to-mabelita-secondary text-white' : 'bg-black text-white';
    } else {
      // Urban (Fondos oscuros)
      textColorClass = 'text-white drop-shadow-md';
      logoColorClass = 'text-white drop-shadow-md';
      iconColorClass = 'text-white drop-shadow-md';

      if (themeLayout === 'urban') {
        marqueeClass = 'bg-[#ccff00] text-black';
      }
    }

    if (themeLayout === 'urban') {
      navBackgroundClass = 'bg-gradient-to-b from-black/80 to-transparent py-4';
    } else {
      navBackgroundClass = 'py-4';
    }
  }

  // --- ANNOUNCEMENT BAR (DYNAMIC MARQUEE) ---
  const bannerEnabled = siteConfig?.banner_enabled !== false; // Default true
  const defaultMarqueeItems = isStorefront
    ? [
      '🧶 NUEVA COLECCIÓN DE INVIERNO DISPONIBLE',
      '✨ ENVÍOS GRATIS A ESPAÑA EN PEDIDOS +100€',
      '🛍️ 10% DTO PRIMERA COMPRA CON CÓDIGO: MABELITA10',
      '🎨 TEJIDOS 100% ARTESANALES EN TELAR ANDINO',
    ]
    : [
      '🎵 ESCUCHA MI ÚLTIMO SINGLE EN SPOTIFY',
      '✨ ENVÍOS GRATIS A ESPAÑA EN PEDIDOS +100€',
      '🧶 NUEVA COLECCIÓN DE INVIERNO DISPONIBLE',
      '🛍️ 10% DTO PRIMERA COMPRA CON CÓDIGO: MABELITA10',
    ];
  const marqueeItems: string[] = (siteConfig?.marquee_items && Array.isArray(siteConfig.marquee_items) && siteConfig.marquee_items.length > 0)
    ? siteConfig.marquee_items
    : defaultMarqueeItems;

  const AnnouncementBar = () => {
    if (!bannerEnabled) return null;
    return (
      <div className={`w-full overflow-hidden whitespace-nowrap py-2 text-xs font-bold tracking-widest uppercase z-[60] relative ${marqueeClass}`}>
        <div className="inline-block animate-marquee">
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <span key={i}><span className="mx-4">{item}</span> •</span>
          ))}
        </div>
      </div>
    );
  };

  // --- RENDER LOGO HELPER ---
  const RenderLogo = () => {
    const isBigLogo = logoPosition === 'inline_big';

    return (
      <div
        className={`cursor-pointer flex items-center gap-3 group relative z-50 ${isBigLogo ? 'py-2' : ''}`}
        onClick={() => {
          if (isStorefront) {
            onNavigate('tejiendo');
          } else {
            onNavigate('home');
          }
        }}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Logo"
            className={`${isBigLogo ? 'h-14 md:h-16' : 'h-12 md:h-14'} w-auto object-contain transition-transform group-hover:scale-105 drop-shadow-md`}
          />
        ) : (
          <div className={`${isBigLogo ? 'w-20 h-20 text-4xl' : 'w-10 h-10 text-xl'} rounded-full flex items-center justify-center font-serif font-bold shadow-sm transition-colors ${themeLayout === 'urban' ? 'bg-[#ccff00] text-black' : 'bg-mabelita-primary text-white'
            }`}>
            M
          </div>
        )}

        {/* MODIFICADO: Ocultar texto si es logo grande */}
        {!isBigLogo && (
          <span className={`font-serif font-bold transition-colors tracking-tight ${logoColorClass} text-xl md:text-2xl`}>
            {isStorefront ? 'Tejiendo con Mabelita' : 'Mabelita de la Cruz'}
          </span>
        )}
      </div>
    );
  };

  // --- MEGA MENU COMPONENT ---
  const MegaMenu = ({ link }: { link: any }) => {
    if (!link.hasMenu || hoveredLink !== link.path) return null;

    return (
      <div
        className="absolute top-full left-1/2 -translate-x-1/2 w-[700px] md:w-[800px] lg:w-[900px] pt-4 animate-in fade-in slide-in-from-top-2 duration-200"
        onMouseEnter={() => setHoveredLink(link.path)}
        onMouseLeave={() => setHoveredLink(null)}
      >
        <div className={`mx-auto w-full rounded-xl shadow-2xl overflow-hidden p-6 grid grid-cols-12 gap-6 relative z-50 ${themeLayout === 'urban'
          ? 'bg-[#1a1a1a] border-4 border-[#ccff00]'
          : 'bg-white/95 backdrop-blur-xl border border-white/20'
          }`}>
          {/* Imagen Miniatura */}
          <div className="col-span-4 relative h-48 rounded-lg overflow-hidden group cursor-pointer" onClick={() => onNavigate(link.path)}>
            <img src={link.thumbnail} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt="Promo" />
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors"></div>
            <div className={`absolute bottom-3 left-3 text-xs font-bold px-2 py-1 rounded ${themeLayout === 'urban' ? 'bg-[#ccff00] text-black' : 'bg-white text-black'
              }`}>
              DESTACADO
            </div>
          </div>

          {/* Texto Persuasivo */}
          <div className="col-span-5 flex flex-col justify-center">
            <h4 className={`text-2xl font-serif font-bold mb-2 ${themeLayout === 'urban' ? 'text-white' : 'text-mabelita-dark'}`}>
              {link.promoTitle}
            </h4>
            <p className={`mb-6 leading-relaxed ${themeLayout === 'urban' ? 'text-gray-400' : 'text-gray-600'}`}>
              {link.promoText}
            </p>
            <button
              onClick={() => onNavigate(link.path)}
              className={`flex items-center gap-2 font-bold text-sm uppercase tracking-widest transition-all group ${themeLayout === 'urban' ? 'text-[#ccff00] hover:text-white' : 'text-mabelita-primary hover:text-mabelita-dark'
                }`}
            >
              {link.ctaText} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Quick Links / Extra CTA */}
          <div className={`col-span-3 border-l pl-6 flex flex-col justify-center ${themeLayout === 'urban' ? 'border-gray-700' : 'border-gray-100'}`}>
            <h5 className={`font-bold text-sm uppercase mb-4 flex items-center gap-2 ${themeLayout === 'urban' ? 'text-gray-300' : 'text-gray-500'}`}>
              <Sparkles size={14} /> {link.subLinks ? 'Colecciones' : 'Lo más buscado'}
            </h5>
            <ul className={`space-y-3 text-sm font-medium ${themeLayout === 'urban' ? 'text-white' : 'text-gray-700'}`}>
              {link.subLinks ? (
                link.subLinks.map((sub: any) => (
                  <li key={sub.path} onClick={() => onNavigate(sub.path)} className="cursor-pointer hover:underline hover:text-mabelita-primary">{sub.name}</li>
                ))
              ) : (
                <>
                  <li className="cursor-pointer hover:underline hover:text-mabelita-primary">Novedades</li>
                  <li className="cursor-pointer hover:underline hover:text-mabelita-primary">Más Vendidos</li>
                  <li className="cursor-pointer hover:underline hover:text-mabelita-primary">Ofertas Especiales</li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // --- RENDER LINKS HELPER ---
  const RenderLinks = () => (
    <div className={`hidden md:flex items-center relative ${logoPosition === 'stacked' ? 'gap-12' : 'space-x-8'}`}>
      {navLinks.map((link) => {
        // Lógica para resaltar link activo considerando hashes
        const isCurrent = currentPage === link.path || (link.path.includes('#') && currentPage === link.path.split('#')[0]);

        const baseColor = isCurrent
          ? (themeLayout === 'urban' ? 'text-[#ccff00]' : 'text-mabelita-accent')
          : textColorClass;

        return (
          <div key={link.path} className="relative group" onMouseEnter={() => setHoveredLink(link.path)} onMouseLeave={() => setHoveredLink(null)}>
            <button
              onClick={() => {
                if ((link as any).external && isStorefront) {
                  window.location.href = `http://${window.location.hostname.replace('tejiendocon.', '')}`;
                } else {
                  handleNavClick(link);
                }
              }}
              className={`text-xs uppercase tracking-wider font-bold transition-all relative py-2 md:py-4 flex items-center gap-2 ${baseColor}`}
            >
              {link.name}

              <span className={`absolute bottom-2 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full ${themeLayout === 'urban' ? 'bg-[#ccff00]' : 'bg-mabelita-accent'
                }`}></span>
            </button>

            {/* INJECT MEGA MENU */}
            {(link as any).hasMenu && <MegaMenu link={link} />}
          </div>
        );
      })}
    </div>
  );

  // --- RENDER ICONS HELPER ---
  const RenderIcons = () => (
    <div className="flex items-center space-x-4 relative z-50">

      {/* BOTÓN ESPECIAL DE CRUCE ENTRE DOMINIOS */}
      {!isStorefront ? (
        <button
          onClick={() => {
            const port = window.location.port ? ':' + window.location.port : '';
            window.open(`http://tejiendocon.${window.location.hostname.replace('www.', '')}${port}`, '_blank');
          }}
          className="hidden md:flex items-center justify-center group mr-4 transition-transform hover:scale-105"
          title="Ir a Tejiendo con Mabelita"
        >
          <img
            src={tejiendoLogoUrl}
            alt="Tejiendo con Mabelita"
            className="h-10 md:h-14 w-auto object-contain drop-shadow-lg filter hover:brightness-110 transition-all"
          />
        </button>
      ) : (
        <button
          onClick={() => {
            const port = window.location.port ? ':' + window.location.port : '';
            window.open(`http://${window.location.hostname.replace('tejiendocon.', '')}${port}`, '_blank');
          }}
          className="hidden md:flex items-center justify-center group mr-4 transition-transform hover:scale-105"
          title="Volver a la Artista"
        >
          <img
            src={tejiendoLogoUrl || ''}
            alt="Mabelita de la Cruz"
            className="h-10 md:h-14 w-auto object-contain drop-shadow-lg filter hover:brightness-110 transition-all"
          />
        </button>
      )}

      {/* Right side icon groups */}
      <div className="flex items-center gap-1 md:gap-3">
        <button
          onClick={() => onNavigate('cart')}
          className={`relative p-2 rounded-full transition-colors hover:bg-black/5 ${iconColorClass}`}
          title="Carrito"
        >
          <ShoppingBag size={24} />
          {cartCount > 0 && (
            <span className={`absolute top-0 right-0 text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full ${themeLayout === 'urban' ? 'bg-[#ccff00] text-black' : 'bg-mabelita-accent text-white'
              }`}>
              {cartCount}
            </span>
          )}
        </button>

        {session ? (
          <div className="hidden md:flex items-center gap-2">
            {/* USER PROFILE DROPDOWN */}
            <div className="relative group">
              <button
                onClick={() => onNavigate('account')}
                className={`flex items-center gap-2 p-2 rounded-full transition-colors hover:bg-black/5 ${iconColorClass}`}
                title="Mi Cuenta"
              >
                <User size={24} />
              </button>
              <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all flex flex-col overflow-hidden z-50">
                <button onClick={() => onNavigate('account')} className="text-left px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                  <User size={16} /> Mi perfil
                </button>
                <button onClick={() => onNavigate('admin')} className="text-left px-4 py-3 text-sm font-bold text-indigo-600 hover:bg-indigo-50 border-t border-gray-50 flex items-center gap-2">
                  <Settings size={16} /> Panel Admin
                </button>
                <button onClick={handleLogout} className="text-left px-4 py-3 text-sm font-bold text-red-600 hover:bg-red-50 border-t border-gray-50 flex items-center gap-2">
                  <LogOut size={16} /> Cerrar Sesión
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={() => onNavigate('auth')}
            className={`hidden md:block px-5 py-2 rounded-full text-sm font-bold transition-all shadow-lg hover:scale-105 ${themeLayout === 'urban'
              ? 'bg-[#ccff00] text-black hover:bg-white'
              : (isScrolled || !isHome ? 'bg-mabelita-primary text-white' : 'bg-white text-mabelita-dark')
              }`}
          >
            Acceder
          </button>
        )}

      </div> {/* End right side icons */}

      {/* Mobile Toggle Icons (Hamburger) */}
      <div className="md:hidden flex items-center gap-2">
        {session && (
          <button
            onClick={() => {
              setIsMobileUserMenuOpen(!isMobileUserMenuOpen);
              setIsMobileMenuOpen(false); // Close main menu if open
            }}
            className={`p-2 transition-colors hover:bg-black/5 ${iconColorClass}`}
          >
            <User size={26} />
          </button>
        )}

        {/* Mobile Hamburger Toggle */}
        <button
          onClick={() => {
            setIsMobileMenuOpen(!isMobileMenuOpen);
            setIsMobileUserMenuOpen(false); // Close user menu if open
          }}
          className={`p-2 ${iconColorClass}`}
        >
          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Global Dynamic Banner */}
      <AnnouncementBar />

      <nav
        className={`fixed ${bannerEnabled ? 'top-[32px]' : 'top-0'} left-0 w-full z-40 transition-all duration-300 ${navBackgroundClass}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">

          {/* LAYOUT: INLINE / INLINE_BIG - Logo Left, Menu Right */}
          {(logoPosition === 'inline' || logoPosition === 'inline_big') && (
            <div className="flex justify-between items-center">
              <RenderLogo />
              <RenderLinks />
              <RenderIcons />
            </div>
          )}

          {/* LAYOUT: CENTERED */}
          {logoPosition === 'centered' && (
            <div className="relative flex items-center justify-between">
              <div className="w-1/3 flex justify-start">
                {/* Left side links (First 2) */}
                <div className="hidden md:flex space-x-6 relative">
                  {navLinks.slice(0, 2).map(link => (
                    <div key={link.path} className="relative group" onMouseEnter={() => setHoveredLink(link.path)} onMouseLeave={() => setHoveredLink(null)}>
                      <button onClick={() => onNavigate(link.path)} className={`font-bold text-sm uppercase py-4 ${textColorClass}`}>{link.name}</button>
                      {(link as any).hasMenu && <MegaMenu link={link} />}
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-1/3 flex justify-center">
                <RenderLogo />
              </div>
              <div className="w-1/3 flex justify-end items-center gap-4">
                <div className="hidden md:flex space-x-6 mr-4 relative">
                  {navLinks.slice(2, 4).map(link => {
                    return (
                      <div key={link.path} className="relative group" onMouseEnter={() => setHoveredLink(link.path)} onMouseLeave={() => setHoveredLink(null)}>
                        <button onClick={() => onNavigate(link.path)} className={`font-bold text-sm uppercase flex items-center gap-2 py-4 ${textColorClass}`}>
                          {link.name}
                        </button>
                        {(link as any).hasMenu && <MegaMenu link={link} />}
                      </div>
                    )
                  })}
                </div>
                <RenderIcons />
              </div>
            </div>
          )}

          {/* LAYOUT: STACKED */}
          {logoPosition === 'stacked' && (
            <div className="flex flex-col items-center py-2">
              <div className="mb-4 transform scale-110">
                <RenderLogo />
              </div>
              <div className="w-full flex justify-between items-center border-t border-white/10 pt-2">
                <div className="w-8"></div> {/* Spacer */}
                <RenderLinks />
                <RenderIcons />
              </div>
            </div>
          )}
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 w-full bg-white shadow-2xl border-t border-gray-100 py-6 px-6 flex flex-col space-y-4 animate-in slide-in-from-top-5 z-50 max-h-[80vh] overflow-y-auto">
            {navLinks.map((link) => (
              <div key={link.path}>
                <button
                  onClick={() => {
                    handleNavClick(link);
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-left text-xl font-serif font-bold text-mabelita-dark border-b border-gray-50 pb-2 flex items-center justify-between gap-3"
                >
                  <span className="flex items-center gap-2">
                    {link.name}
                  </span>
                </button>
                {/* Mobile Sublinks */}
                {(link as any).hasMenu && ((link as any).subLinks || (link as any).menuItems) && (
                  <div className="mt-2 mb-4 flex flex-col gap-1 pl-4 border-l-2 border-gray-100">
                    {((link as any).subLinks || (link as any).menuItems).map((sub: any) => (
                      <button
                        key={sub.name || sub.title}
                        onClick={() => {
                          if ((sub as any).external) {
                            window.open(sub.path, '_blank');
                          } else {
                            onNavigate(sub.path);
                          }
                          setIsMobileMenuOpen(false);
                        }}
                        className="text-left w-full py-2 px-3 text-sm font-medium text-gray-600 hover:text-mabelita-primary transition-colors flex items-center gap-3"
                      >
                        {sub.icon || <ArrowRight size={14} className="text-mabelita-primary" />} {sub.name || sub.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Mobile Cross-Domain Logo */}
            {!isStorefront ? (
              <button
                onClick={() => {
                  const port = window.location.port ? ':' + window.location.port : '';
                  window.open(`http://tejiendocon.${window.location.hostname.replace('www.', '')}${port}`, '_blank');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex justify-center py-6 border-t border-gray-100 mt-2 hover:bg-black/5 transition-colors"
                title="Ir a Tejiendo con Mabelita"
              >
                <img src={tejiendoLogoUrl} className="h-16 w-auto object-contain drop-shadow-md" alt="Tejiendo con Mabelita" />
              </button>
            ) : (
              <button
                onClick={() => {
                  const port = window.location.port ? ':' + window.location.port : '';
                  window.open(`http://${window.location.hostname.replace('tejiendocon.', '')}${port}`, '_blank');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex flex-col items-center gap-2 py-4 border-t border-gray-100 mt-2 bg-gradient-to-b from-[#F5F1E8] to-white rounded-xl hover:shadow-md transition-all"
              >
                <img src={tejiendoLogoUrl || ''} className="h-14 w-auto object-contain drop-shadow-sm" alt="Volver a la Artista" />
                <span className="text-xs font-bold text-mabelita-dark/60 uppercase tracking-wider">Mabelita Artista</span>
              </button>
            )}

            {/* Mobile Auth Option (if not logged in) */}
            {!session && (
              <div className="pt-4 border-t border-gray-100">
                <button onClick={() => { onNavigate('auth'); setIsMobileMenuOpen(false); }} className="w-full bg-mabelita-primary hover:bg-mabelita-dark text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors shadow-md">
                  <User size={20} /> Iniciar Sesión / Registrarse
                </button>
              </div>
            )}
          </div>
        )}

        {/* Mobile User Dropdown Menu */}
        {isMobileUserMenuOpen && (
          <div className="absolute top-full left-0 w-full bg-white shadow-2xl border-t border-gray-100 py-6 px-6 flex flex-col space-y-4 animate-in slide-in-from-top-5 z-50">
            <div className="pb-2 border-b border-gray-100 mb-2">
              <h4 className="font-serif font-bold text-lg text-mabelita-dark">Mi Perfil</h4>
            </div>
            <div className="flex flex-col gap-3">
              {session ? (
                <>
                  <button onClick={() => { onNavigate('account'); setIsMobileUserMenuOpen(false); }} className="text-left bg-gray-50 hover:bg-gray-100 p-4 rounded-xl font-bold text-gray-800 flex items-center gap-3 transition-colors">
                    <User size={20} /> Mi Cuenta
                  </button>
                  <button onClick={() => { onNavigate('admin'); setIsMobileUserMenuOpen(false); }} className="text-left bg-gray-50 hover:bg-gray-100 p-4 rounded-xl font-bold text-gray-800 flex items-center gap-3 transition-colors">
                    <Briefcase size={20} /> Panel Administración
                  </button>
                  <button onClick={() => { handleLogout(); setIsMobileUserMenuOpen(false); }} className="text-left bg-red-50 hover:bg-red-100 mt-2 p-4 rounded-xl font-bold text-red-600 flex items-center gap-3 transition-colors">
                    <LogOut size={20} className="text-red-500" /> Cerrar Sesión
                  </button>
                </>
              ) : (
                <button onClick={() => { onNavigate('auth'); setIsMobileUserMenuOpen(false); }} className="bg-mabelita-primary hover:bg-mabelita-dark transition-colors text-white py-4 px-4 rounded-xl font-bold text-center shadow-lg w-full flex justify-center items-center gap-2">
                  <User size={20} /> Iniciar Sesión / Registrarse
                </button>
              )}
            </div>
          </div>
        )}
      </nav>
      {/* Spacer for fixed nav */}
      <div className={`${bannerEnabled ? 'h-[100px] md:h-[120px]' : 'h-[68px] md:h-[88px]'} w-full bg-transparent`}></div>
    </>
  );
};
